import React, { useEffect, useState } from 'react';
import { BadgeCheck, Clock, Loader2, TriangleAlert } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button, Surface, Text } from '@/components/flup';
import { cn } from '@/lib/utils';
import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks';
import { RootState } from '@/store';
import { getVenueMonetisation } from '@/store/thunkMidddleware/monetisationMiddleware';
import {
  VenueMonetisation,
  formatPaise,
  isAwaitingPayment,
  isPlanChangeLocked,
  isZeroCommission,
  planLabel,
} from '@/types/monetisationTypes';
import PlanSwitcherModal from './PlanSwitcherModal';

/**
 * Every venue's plan, in one list.
 *
 * A plan is per venue, so a vendor with four venues has four answers to "which
 * plan am I on" — this is the screen that shows all of them, and the switcher
 * always opens against one named venue.
 *
 * Plans are fetched when this opens rather than on app load: the venue record
 * is created lazily by the API's first read, so touching a venue that has not
 * paid yet would mint a plan record for a venue that cannot use one.
 */

export interface PlanVenue {
  _id: string;
  name?: string;
  oneTimeOnboardingFeeDetails?: { isPaymentdone?: boolean };
}

interface VenuePlansModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  venues: PlanVenue[];
  onSubscriptionCheckout: (checkout: Record<string, any>) => void;
}

const isLive = (venue: PlanVenue) => Boolean(venue?.oneTimeOnboardingFeeDetails?.isPaymentdone);

const VenuePlansModal: React.FC<VenuePlansModalProps> = ({
  open,
  onOpenChange,
  venues,
  onSubscriptionCheckout,
}) => {
  const dispatch = useAppDispatch();
  const { venuePlans } = useAppSelector((state: RootState) => state.monetisation);

  const [switcherVenue, setSwitcherVenue] = useState<PlanVenue | null>(null);

  useEffect(() => {
    if (!open) return;
    venues.filter(isLive).forEach((venue) => {
      dispatch(getVenueMonetisation({ venueId: venue._id }));
    });
  }, [open, venues.length]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Plans</DialogTitle>
            <DialogDescription>
              Each venue is billed on its own plan. Switch any of them independently.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            {venues.map((venue) => {
              const plan: VenueMonetisation | undefined = venuePlans[venue._id];
              const live = isLive(venue);
              const onSubscription = plan?.plan === 'SUBSCRIPTION';
              const awaitingPayment = isAwaitingPayment(plan);
              const pending = awaitingPayment ? undefined : plan?.pendingPlanChange;
              // A granted plan (founding partner) is fixed for its term: no
              // switch to offer, and no mandate of its own to be broken.
              const locked = isPlanChangeLocked(plan);
              const zeroCommission = isZeroCommission(plan?.plan);
              const mandateBroken =
                onSubscription &&
                !locked &&
                plan?.mandate?.status &&
                plan.mandate.status !== 'ACTIVE';

              return (
                <Surface
                  key={venue._id}
                  padding="p-3"
                  className="flex items-center gap-3 bg-flup-surface-alt"
                >
                  <div className="min-w-0 flex-1">
                    <Text variant="labelSemi" as="p" lines={1}>
                      {venue.name || 'Untitled venue'}
                    </Text>

                    {!live ? (
                      <Text variant="micro" tone="tertiary" as="p" className="mt-0.5">
                        Not live yet — a plan is chosen when you go live
                      </Text>
                    ) : !plan ? (
                      <Text variant="micro" tone="tertiary" as="p" className="mt-0.5">
                        Loading…
                      </Text>
                    ) : (
                      <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                        <BadgeCheck
                          className={cn(
                            'w-3.5 h-3.5 shrink-0',
                            locked
                              ? 'text-flup-brand'
                              : zeroCommission
                                ? 'text-flup-green'
                                : 'text-flup-ink-3',
                          )}
                          aria-hidden
                        />
                        <Text variant="micro" tone="tertiary" as="span">
                          {locked
                            ? `${planLabel(plan.plan)}${
                                typeof (plan.foundingPartner?.daysRemaining ??
                                  plan.switchEligibility?.daysRemaining) === 'number'
                                  ? ` · ${
                                      plan.foundingPartner?.daysRemaining ??
                                      plan.switchEligibility?.daysRemaining
                                    } days left`
                                  : ''
                              }`
                            : onSubscription
                              ? `Zero commission · ${formatPaise(plan.payablePaise)} ${
                                  plan.billingCycle ?? ''
                                }`
                              : 'Pay per booking'}
                        </Text>

                        {awaitingPayment ? (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3 text-flup-warning" aria-hidden />
                            <Text variant="micro" tone="tertiary" as="span">
                              payment pending
                            </Text>
                          </span>
                        ) : null}

                        {pending ? (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3 text-flup-warning" aria-hidden />
                            <Text variant="micro" tone="tertiary" as="span">
                              switching
                            </Text>
                          </span>
                        ) : null}

                        {mandateBroken && !pending ? (
                          <span className="flex items-center gap-1">
                            <TriangleAlert className="w-3 h-3 text-flup-warning" aria-hidden />
                            <Text variant="micro" tone="tertiary" as="span">
                              auto-debit off
                            </Text>
                          </span>
                        ) : null}
                      </div>
                    )}
                  </div>

                  {live ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={!plan}
                      onClick={() => setSwitcherVenue(venue)}
                    >
                      {plan ? (
                        locked ? 'View' : awaitingPayment ? 'Pay' : pending ? 'Manage' : 'Switch'
                      ) : (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden />
                      )}
                    </Button>
                  ) : null}
                </Surface>
              );
            })}

            {!venues.length ? (
              <Text variant="body" tone="secondary" as="p">
                No venues yet. Add one to choose a plan.
              </Text>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      <PlanSwitcherModal
        open={Boolean(switcherVenue)}
        onOpenChange={(isOpen) => {
          if (!isOpen) setSwitcherVenue(null);
        }}
        venueId={switcherVenue?._id ?? null}
        venueName={switcherVenue?.name}
        onSubscriptionCheckout={onSubscriptionCheckout}
      />
    </>
  );
};

export default VenuePlansModal;
